/**
 * Excel (.xlsx) export for dashboard sections.
 *
 * Sections describe *what* to export (sheets → columns → rows) and this
 * module owns *how*: a bold frozen header in row 1 with autofilter (so
 * the file sorts / filters in Excel and imports cleanly into mapping
 * tools like Google My Maps), sensible column widths and number formats,
 * and a print setup — landscape, fit to page width, header row repeated
 * on every page — so a list prints straight off for a visit plan.
 *
 * Every file also ends with an "Export Info" sheet — who exported it,
 * when, and with which filters — so a printout or forwarded file explains
 * itself (the same idea as Power BI's "Applied filters" line, kept off
 * the data sheets so row 1 stays a clean header).
 *
 * Files are named `HY_<Section>[_<Scope>]_<User>_<YYYY-MM-DD>_<HHmm>.xlsx`
 * (see `exportFilename`): ISO date so files sort chronologically, no
 * spaces, one fixed part order across the whole app.
 *
 * `exceljs` is ~1 MB, so it's loaded on demand the first time someone
 * clicks Export rather than shipped in the main bundle.
 */

/** A cell value the exporter knows how to write. */
type ExcelCell = string | number | boolean | Date | null | undefined;

type ExcelColumnFormat = 'text' | 'int' | 'decimal' | 'percent' | 'date';

export interface ExcelColumn<T> {
  header: string;
  value: (row: T) => ExcelCell;
  /** Width in characters. Defaults by format. */
  width?: number;
  /** Number format. Defaults to 'text' (written as-is). */
  format?: ExcelColumnFormat;
}

interface ExcelSheet<T = never> {
  /** Tab name. Truncated to Excel's 31-char limit. */
  name: string;
  columns: ExcelColumn<T>[];
  rows: readonly T[];
}

/** One applied filter / view setting, listed on the Export Info sheet. */
/**
 * One line on the Export Info sheet.
 *
 * - `filter` (default): a control the user set — name it exactly as the
 *   control is labelled in the UI ('State', 'Search', 'Time window').
 *   List it even when unused; an empty value prints as 'None'.
 * - `sort`: the sort order in effect.
 * - `info`: a fact about the data, not a choice (Data through, Months
 *   covered, comparison windows). Skipped when empty.
 */
export interface ExportFilter {
  label: string;
  value: string;
  kind?: 'filter' | 'sort' | 'info';
}

/** Plain-English "what is this report" copy for the Export Info sheet. */
export interface ExportAbout {
  /** One or two sentences: what the section shows. */
  description: string;
}

export interface ExcelWorkbookSpec {
  /** Section name — leads the filename and the printed page header. */
  title: string;
  /** Optional sub-view that changes what the file holds (e.g. a bucket
   *  tab like '30-60 days'). Added to the filename after the title. */
  scope?: string;
  /** What the section is, for the Export Info sheet. */
  about?: ExportAbout;
  /** Filters / view settings in effect, for the Export Info sheet. */
  filters?: ExportFilter[];
  // Sheets carry heterogeneous row types; each sheet is internally typed
  // by `excelSheet()` below.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sheets: ExcelSheet<any>[];
}

/** Typed constructor so each sheet's columns are checked against its rows. */
export function excelSheet<T>(sheet: ExcelSheet<T>): ExcelSheet<T> {
  return sheet;
}

/** Decimal-as-string (how the API sends Pydantic Decimals) → number. */
export function num(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/** ISO 'YYYY-MM-DD' → a Date that Excel shows as that same calendar day. */
export function isoDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  if (!y || !m) return null;
  return new Date(Date.UTC(y, m - 1, d || 1));
}

const NUM_FMT: Record<ExcelColumnFormat, string | undefined> = {
  text: undefined,
  int: '#,##0',
  decimal: '#,##0.0',
  percent: '0.0%',
  date: 'yyyy-mm-dd',
};

/**
 * Workbook palette — Office's standard "Blue, Accent 1" table colours, the
 * look people expect from a business spreadsheet. Headers are dark text on
 * a light tint (not white on a dark band): every pairing passes WCAG AAA
 * contrast (≥ 7:1), meaning never depends on hue so it reads the same for
 * colour-blind users, and it prints cleanly on black-and-white printers.
 */
const PALETTE = {
  headerFill: 'FFD9E1F2', // Blue, Accent 1, lighter 80%
  headerText: 'FF1F3864', // Blue, Accent 1, darker 50% — 8.9:1 on headerFill
  accentRule: 'FF4472C4', // Blue, Accent 1 — underline under headers
  labelFill: 'FFF2F2F2', // White, darker 5%
  labelText: 'FF262626', // 13.5:1 on labelFill
  gridLine: 'FFBFBFBF',
} as const;

const solid = (argb: string) => ({
  type: 'pattern' as const,
  pattern: 'solid' as const,
  fgColor: { argb },
});
const thinLine = { style: 'thin' as const, color: { argb: PALETTE.gridLine } };
const accentRule = { style: 'medium' as const, color: { argb: PALETTE.accentRule } };

const DEFAULT_WIDTH: Record<ExcelColumnFormat, number> = {
  text: 18,
  int: 10,
  decimal: 12,
  percent: 10,
  date: 12,
};

// Excel forbids these in sheet names.
const BAD_SHEET_CHARS = /[\\/?*[\]:]/g;

const pad = (n: number) => String(n).padStart(2, '0');

/** Who is exporting — shown on the Export Info sheet and in the filename. */
interface Exporter {
  name: string;
  email?: string | null;
}

/** 'New vs Lost Accounts' → 'New-vs-Lost-Accounts'; '120+ days' → '120plus-days'. */
function filenamePart(text: string): string {
  return text
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, 'and')
    .replace(/\+/g, 'plus')
    .replace(/['’]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * The app-wide export filename:
 * `HY_<Section>[_<Scope>]_<User>_<YYYY-MM-DD>_<HHmm>.xlsx`, local time.
 * e.g. `HY_New-vs-Lost-Accounts_Jane-Smith_2026-10-06_1432.xlsx`.
 */
function exportFilename(
  spec: Pick<ExcelWorkbookSpec, 'title' | 'scope'>,
  exporter: Exporter,
  at: Date = new Date(),
): string {
  const date = `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
  const time = `${pad(at.getHours())}${pad(at.getMinutes())}`;
  const parts = ['HY', filenamePart(spec.title)];
  if (spec.scope) parts.push(filenamePart(spec.scope));
  parts.push(filenamePart(exporter.name) || 'User', date, time);
  return `${parts.filter(Boolean).join('_')}.xlsx`;
}

/** '2026-10-06 14:32 CDT' — local wall time plus zone, for humans. */
function humanTimestamp(at: Date): string {
  const tz =
    new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
      .formatToParts(at)
      .find((p) => p.type === 'timeZoneName')?.value ?? '';
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())} ${pad(
    at.getHours(),
  )}:${pad(at.getMinutes())} ${tz}`.trim();
}

export interface ExportResult {
  filename: string;
  /** Data rows across all sheets (excludes headers and Export Info). */
  rowCount: number;
}

/** Build the workbook and trigger a browser download. */
export async function downloadExcel(
  spec: ExcelWorkbookSpec,
  exporter: Exporter,
): Promise<ExportResult> {
  const { default: ExcelJS } = await import('exceljs');
  const at = new Date();
  const wb = new ExcelJS.Workbook();
  wb.creator = exporter.name;
  wb.lastModifiedBy = exporter.name;
  wb.company = 'Hooten Young';
  wb.title = spec.scope ? `${spec.title} — ${spec.scope}` : spec.title;
  wb.created = at;
  const stamp = humanTimestamp(at);

  const usedNames = new Set<string>();
  for (const sheet of spec.sheets) {
    let name = sheet.name.replace(BAD_SHEET_CHARS, ' ').trim().slice(0, 31) || 'Sheet';
    for (let i = 2; usedNames.has(name.toLowerCase()); i++) {
      name = `${name.slice(0, 28)} ${i}`;
    }
    usedNames.add(name.toLowerCase());

    const ws = wb.addWorksheet(name, {
      views: [{ state: 'frozen', ySplit: 1 }],
      pageSetup: {
        orientation: 'landscape',
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 0,
        printTitlesRow: '1:1',
        margins: { left: 0.4, right: 0.4, top: 0.6, bottom: 0.6, header: 0.3, footer: 0.3 },
      },
      headerFooter: {
        oddHeader: `&L&"-,Bold"${spec.title.replace(/&/g, '&&')} — ${sheet.name.replace(/&/g, '&&')}&RExported ${stamp}`,
        oddFooter: `&LHooten Young — internal · ${exporter.name.replace(/&/g, '&&')}&RPage &P of &N`,
      },
    });

    ws.columns = sheet.columns.map((c) => {
      const format = c.format ?? 'text';
      return {
        header: c.header,
        width: c.width ?? Math.max(DEFAULT_WIDTH[format], c.header.length + 2),
        style: NUM_FMT[format] ? { numFmt: NUM_FMT[format] } : {},
      };
    });

    for (const row of sheet.rows) {
      ws.addRow(sheet.columns.map((c) => c.value(row) ?? null));
    }

    // Style header cells individually — a row-level fill would paint every
    // column out to XFD.
    const header = ws.getRow(1);
    header.height = 20;
    header.eachCell((c) => {
      c.font = { bold: true, color: { argb: PALETTE.headerText } };
      c.fill = solid(PALETTE.headerFill);
      c.alignment = { vertical: 'middle' };
      c.border = { top: thinLine, left: thinLine, right: thinLine, bottom: accentRule };
    });
    if (sheet.columns.length > 0) {
      ws.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: Math.max(1, sheet.rows.length + 1), column: sheet.columns.length },
      };
    }
  }

  // Export Info — last, so the data sheet is what Excel opens on. Grouped
  // so a reader can tell the filters that shaped the rows from facts about
  // the data and the export itself.
  const entries = spec.filters ?? [];
  const ofKind = (k: NonNullable<ExportFilter['kind']>) =>
    entries.filter((f) => (f.kind ?? 'filter') === k);
  const filterRows = ofKind('filter').map((f): [string, string] => [
    f.label,
    f.value.trim() === '' ? 'None' : f.value,
  ]);
  const groups: [string, [string, string][]][] = [
    [
      'Export details',
      [
        ['Report', spec.title],
        ...(spec.scope ? ([['View', spec.scope]] as [string, string][]) : []),
        ['Exported by', exporter.email ? `${exporter.name} <${exporter.email}>` : exporter.name],
        ['Exported at', stamp],
      ],
    ],
    ['About this report', spec.about ? [['What it shows', spec.about.description]] : []],
    [
      'Filters applied',
      filterRows.length > 0 ? filterRows : [['Filters', 'None — all data in this section']],
    ],
    ['Sort order', ofKind('sort').map((f): [string, string] => [f.label, f.value])],
    [
      'Data coverage',
      ofKind('info')
        .filter((f) => f.value.trim() !== '')
        .map((f): [string, string] => [f.label, f.value]),
    ],
    [
      'Sheets in this file',
      spec.sheets.map((sh): [string, string] => [
        sh.name,
        `${sh.rows.length.toLocaleString()} ${sh.rows.length === 1 ? 'row' : 'rows'}`,
      ]),
    ],
    ['Source', [['Source', 'Hooten Young Dashboard — internal use only']]],
  ];

  const info = wb.addWorksheet('Export Info', {
    views: [{ showGridLines: false }],
    pageSetup: { orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });
  info.columns = [{ width: 28 }, { width: 64 }];
  const titleRow = info.addRow([`${wb.title} — Export Info`]);
  titleRow.font = { bold: true, size: 14, color: { argb: PALETTE.headerText } };
  info.mergeCells(titleRow.number, 1, titleRow.number, 2);
  for (const [heading, rows] of groups) {
    if (rows.length === 0) continue;
    info.addRow([]);
    const h = info.addRow([heading.toUpperCase()]);
    info.mergeCells(h.number, 1, h.number, 2);
    h.height = 20;
    const hc = h.getCell(1);
    hc.font = { bold: true, size: 10, color: { argb: PALETTE.headerText } };
    hc.fill = solid(PALETTE.headerFill);
    hc.alignment = { vertical: 'middle' };
    hc.border = { top: thinLine, left: thinLine, right: thinLine, bottom: accentRule };
    for (const [label, value] of rows) {
      const r = info.addRow([label, value]);
      r.alignment = { vertical: 'top', wrapText: true };
      // Excel won't grow a row for wrapped text written by a library, so
      // size it from the text: ~70 chars per line in the 64-wide column.
      const lines = value
        .split('\n')
        .reduce((n, line) => n + Math.max(1, Math.ceil(line.length / 70)), 0);
      if (lines > 1) r.height = 15 * lines + 2;
      const lc = r.getCell(1);
      lc.font = { bold: true, color: { argb: PALETTE.labelText } };
      lc.fill = solid(PALETTE.labelFill);
      r.eachCell((c) => {
        c.border = { top: thinLine, bottom: thinLine, left: thinLine, right: thinLine };
      });
    }
  }

  const filename = exportFilename(spec, exporter, at);
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the browser a tick to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return { filename, rowCount: spec.sheets.reduce((n, sh) => n + sh.rows.length, 0) };
}

/**
 * Shared column set for anything account-shaped — keeps every export's
 * address block identical (Name, Address, City, County, State, ZIP), the
 * order a mapping import or a printed route sheet expects.
 */
interface AccountLike {
  name: string;
  address?: string | null;
  city?: string | null;
  county?: string | null;
  state_code?: string | null;
  zip_code?: string | null;
}

export function accountColumns<T extends AccountLike>(): ExcelColumn<T>[] {
  return [
    { header: 'Account', value: (r) => r.name, width: 34 },
    { header: 'Address', value: (r) => r.address, width: 30 },
    { header: 'City', value: (r) => r.city, width: 18 },
    { header: 'County', value: (r) => r.county, width: 14 },
    { header: 'State', value: (r) => r.state_code, width: 7 },
    { header: 'ZIP', value: (r) => r.zip_code, width: 8 },
  ];
}

/** Human premises label matching the UI's chips. */
export function premisesLabel(
  premises: string | null | undefined,
  isOnline?: boolean | null,
): string {
  if (isOnline) return 'Online';
  switch (premises) {
    case 'ON':
      return 'On-premise';
    case 'OFF':
      return 'Off-premise';
    case 'NA':
      return 'N/A';
    default:
      return 'Unknown';
  }
}
